import * as React from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { useHistory, useLocation } from "react-router-dom";
import ACTIONS from "../actions";
import {
  REACT_APP_API_BASE_URL,
  SNACKBAR_VARIANT,
  REACT_APP_BEFORE_LOGIN_REDIRECT_URL,
  CHAT_ROOMS,
  REACT_APP_STRIPE_PUBLIC_KEY,
  ONLINE_STATUS,
  MESSAGE_FILE_TYPES,
} from "../../config/index";
import MESSAGE from "../../config/messages";
import SOCKET_EVENTS from "../../config/SocketEvents";
import CUSTOM_EVENTS from "../../constants/customEvents";
import useCustomSnackbar from "../../store/contexts/SnackbarContext";
import { ROUTES } from "../../routes/User/paths";
import notificationSound from "../../utils/notificationSound";
import capitalizeEachFirstWord from "../../utils/CapLetters";

const {
  DATA_LOADING,
  SET_AUTH_TOKENS,
  SET_USER_DATA,
  SET_ALL_CHATS,
  UPDATE_ALL_CHATS_LIST,
  SET_ALL_GROUP_CHATS,
  FILTER_CHATS,
  SET_ACTIVE_CHAT,
  UPDATE_ACTIVE_CHAT_DATA,
  UPDATE_ACTIVE_CHAT_LIST,
  UPDATE_CHATS,
  UPDATE_USER_ONLINE_STATUS,
  UPDATE_CHAT_ONLINE_STATUS,
  ADD_WAITING_ROOM_PARTICIPANT_REQUESTS,
  UPDATE_WAITING_ROOM_PARTICIPANT_REQUESTS,
  CLEAR_EXPIRED_JOIN_REQUESTS,
  UPDATE_USER_BAN_STATUS_IN_ACTIVE_ROOM,
  UPDATE_USER_TOOLBOX_BAN_STATUS_IN_ACTIVE_ROOM,
  UPDATE_USER_MODERATORSHIP_STATUS_IN_ACTIVE_ROOM,
  UPDATE_USER_KICK_OUT_VOTE_IN_ACTIVE_ROOM,
  UPDATE_NEW_ROOM_PARTICIPANT_IN_ACTIVE_ROOM,
  UPDATE_DELETED_CHAT_MESSAGE,
  UPDATE_EDITED_CHAT_MESSAGE,
  UPDATE_MUTED_ROOM_PARTICIPANT_IN_ACTIVE_ROOM,
  UPDATE_MESSAGE_INTERACTION_IN_ACTIVE_ROOM,
  UPDATE_MESSAGE_TIPPING_IN_ACTIVE_ROOM,
  UPDATE_ROOM_TEAM_SCORE_IN_ACTIVE_ROOM,
  UPDATE_USER_DATA,
  UPDATE_EXPIRED_ACTIVE_CHAT,
  MARK_ROOM_MESSAGES_READ,
  UPDATE_CHAT_PREF,
  REMOVE_CHAT,
  ADD_OPTIMISTIC_MESSAGE,
  FAIL_OPTIMISTIC_MESSAGE,
  EXPIRE_MESSAGES,
  ADD_BLOCKED_PERIOD_MESSAGE,
  ADD_SYSTEM_NOTICE,
  KEEP_MESSAGE,
  UNKEEP_MESSAGE,
} = ACTIONS;

export const AppContext = React.createContext();

// Module-level guards — prevent duplicate side effects when 55+ components call useApp()
let _accountFetchInitiated = false;
let _refreshTokenPromise = null; // singleton in-flight refresh promise
let _initialDataFetchedForUserId = null; // track which user's initial data (rooms, chats) was loaded

function useApp() {
  const context = React.useContext(AppContext);

  if (!context) {
    throw new Error("useAdminContext must be used within an AuthProvider");
  }

  const { showSnackbar } = useCustomSnackbar();
  const history = useHistory();
  const location = useLocation();
  const currentPageURL = location.pathname;

  const [appState, dispatch, popupNotifications, setPopupNotifications] = context;
  // typingByRoom: { [roomId]: { [senderId]: true } }  — updated from USER_TYPING socket events
  const [typingByRoom, setTypingByRoom] = React.useState({});
  // localSystemMessages: { [roomId]: [{ id, text, ts }] } — never sent to backend, only visible to this user
  const [localSystemMessages, setLocalSystemMessages] = React.useState({});
  const addLocalSystemMessage = React.useCallback((roomId, text) => {
    if (!roomId) return;
    setLocalSystemMessages(prev => ({
      ...prev,
      [roomId]: [...(prev[roomId] || []), { id: `sys_${Date.now()}`, text, ts: Date.now() }],
    }));
  }, []);

  // Map of tempId → timeout handle for optimistic message failure detection
  const pendingTimersRef = React.useRef({});

  // Always-current refs so the NEW_MESSAGE handler never reads stale closure values
  const activeChatIdRef = React.useRef(null);
  const currentUserIdRef = React.useRef(null);
  const mutedRoomsRef = React.useRef({});

  const {
    accessToken,
    refreshToken,
    socket,
    userData,
    chatsPaginations,
    activeChat,
    waitingRoomParticipantRequests,
    roomToolbox,
  } = appState;

  axios.defaults.baseURL = REACT_APP_API_BASE_URL;
  // axios.defaults.headers.post["Content-Type"] = "application/json";

  /* URLs for auths, just before login */
  const URLsBeforeLogin = [
    ROUTES.forgotPassword,
    ROUTES.register,
    ROUTES.resetPassword,
    ROUTES.root,
  ];

  const exceptionalURLs = [ROUTES.verifyAccount];

  // Persist pending join requests across page refreshes
  React.useEffect(() => {
    try {
      localStorage.setItem(
        "waitingRoomParticipantRequests",
        JSON.stringify(waitingRoomParticipantRequests)
      );
    } catch {}
  }, [waitingRoomParticipantRequests]);

  const dataLoadinHandler = (status) => {
    dispatch({
      type: DATA_LOADING,
      payload: status,
    });
  };

  const isAdminRoute = currentPageURL.startsWith("/admin");

  const loadANewPage = React.useCallback((page) => {
    return (window.location.href = page);
  }, []);

  const setAuthTokens = React.useCallback(({ accessToken, refreshToken }) => {
    dispatch({
      type: SET_AUTH_TOKENS,
      payload: { accessToken, refreshToken },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetApp = React.useCallback(() => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("waitingRoomParticipantRequests");
    _accountFetchInitiated = false;
    _initialDataFetchedForUserId = null;
    // Cancel all pending disappearing-message timers so they don't fire after logout
    Object.values(timerMapRef.current).forEach(clearTimeout);
    timerMapRef.current = {};
    setAuthTokens({
      accessToken: "",
      refreshToken: "",
    });
    return loadANewPage(ROUTES.login);
  }, [setAuthTokens, loadANewPage]);

  React.useEffect(() => {
    /* Get user account details — guarded by module-level flag so only ONE of the
       55+ components that call useApp() actually fires the request on mount. */
    if (
      !_accountFetchInitiated &&
      accessToken &&
      !URLsBeforeLogin.includes(currentPageURL) &&
      !exceptionalURLs.includes(currentPageURL) &&
      !isAdminRoute &&
      Object.keys(userData).length === 0
    ) {
      _accountFetchInitiated = true;
      GetAccountApiCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    socket
      .off(SOCKET_EVENTS.SESSION_TERMINATED)
      .on(SOCKET_EVENTS.SESSION_TERMINATED, (data) => {
        console.log("Session termination event received:", data);

        // ONLY handle admin actions
        if (data.reason === "admin_action") {
          showSnackbar(
            data.message ||
              "Your session has been terminated by an administrator. Please log in again.",
            SNACKBAR_VARIANT.ERROR,
          );

          socket.emit(SOCKET_EVENTS.SESSION_TERMINATED_ACK);

          setTimeout(() => {
            resetApp();
          }, 2000);
        } else {
          // Ignore non-admin terminations
          console.log("Ignoring non-admin session termination:", data.reason);
        }
      });
  }, [socket, showSnackbar, resetApp]);

  React.useEffect(() => {
    if (!userData?.id) return;
    // Guard: only the first component to see this userId fires the initial data load.
    // Without this, all 55+ useApp() callers would each emit a socket request and
    // call GetAllRoomsApiCall when userData first becomes available.
    if (_initialDataFetchedForUserId === userData.id) return;
    _initialDataFetchedForUserId = userData.id;
    socket.emit(SOCKET_EVENTS.LOAD_ALL_GROUP_CHATS_REQUEST);
    GetAllRoomsApiCall();
    socket
      .off(SOCKET_EVENTS.LOAD_USER_CHATS_RESULT)
      .on(SOCKET_EVENTS.LOAD_USER_CHATS_RESULT, async (result) => {
        dispatch({ type: SET_ALL_CHATS, payload: result });
        // Schedule per-message timers for every disappearing message just loaded
        (result.conversations || []).forEach((chat) => {
          scheduleDisappearingTimers(chat.messages, chat.id);
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  // Add popup notification
  const addPopupNotification = React.useCallback((notification) => {
    const id = `notif-${Date.now()}-${Math.random()}`;
    setPopupNotifications(prev => [{
      ...notification,
      id,
      timestamp: new Date().toISOString(),
    }, ...prev].slice(0, 5));
  }, [setPopupNotifications]);

  // Dismiss popup notification
  const dismissPopupNotification = React.useCallback((id) => {
    setPopupNotifications(prev => prev.filter(notif => notif.id !== id));
  }, [setPopupNotifications]);

  // Keep refs current on every render so the socket handler never reads stale values
  activeChatIdRef.current = activeChat?.id ?? null;
  currentUserIdRef.current = userData?.id ?? null;
  mutedRoomsRef.current = Object.fromEntries(
    (appState.chats || []).map(c => [c.id?.toString(), !!c.is_muted])
  );

  React.useEffect(() => {
    if (Object.keys(userData).length > 0) {
      //This will handle the service when a message received and to be disbursed across the app
      socket
        .off(SOCKET_EVENTS.NEW_MESSAGE)
        .on(SOCKET_EVENTS.NEW_MESSAGE, async (result) => {
          // Clear the failure timer for this message if it was optimistic
          if (result.tempId) {
            const timer = pendingTimersRef.current[result.tempId];
            if (timer) {
              clearTimeout(timer);
              delete pendingTimersRef.current[result.tempId];
            }
          }

          const content = result.data;
          // Attach tempId to payload so UPDATE_CHATS atomically removes the optimistic
          // and adds the real message in ONE state update — eliminates the flicker
          if (result.tempId) content._tempIdToConfirm = result.tempId;


          //Update user's message
          dispatch({
            type: UPDATE_CHATS,
            payload: content,
          });

          // Schedule per-message timer if this message will disappear
          if (content?.message?.disappears_at && !content?.message?.is_kept) {
            scheduleDisappearingTimers([content.message], content.id);
          }

          // Show popup notification ONLY if message is from another user and not in active chat
          if (!currentUserIdRef.current || !content?.message) {
            return;
          }

          // Read latest values from refs (never stale, even if the effect didn't re-run)
          const currentUserId = currentUserIdRef.current?.toString();
          const senderId = content.message.sender_id?.toString();
          const messageRoomId = (content.room_id || content.id)?.toString();
          const isFromCurrentUser = senderId === currentUserId;
          const isInActiveChat = activeChatIdRef.current && messageRoomId === activeChatIdRef.current?.toString();

          // Only show notification if:
          // 1. Message is NOT from current user
          // 2. Message is NOT in the currently active chat
          // 3. Chat is NOT muted
          const isRoomMuted = mutedRoomsRef.current[messageRoomId];
          if (!isFromCurrentUser && !isInActiveChat && !isRoomMuted) {
            const messageData = content.message;
            let senderName = 'Someone';
            let senderAvatar = null;

            if (messageData.sender_username) {
              senderName = messageData.sender_username;
              senderAvatar = messageData.sender_avatar || null;
            }

            if (senderName === 'Someone' && content.room_participants && Array.isArray(content.room_participants)) {
              const sender = content.room_participants.find(
                p => p.id?.toString() === senderId
              );
              if (sender) {
                senderName = sender.username || sender.name || senderName;
                senderAvatar = sender.avatar || null;
              }
            }

            if (senderName === 'Someone' && messageData.sender) {
              senderName = messageData.sender.username || messageData.sender.name || senderName;
              senderAvatar = messageData.sender.avatar || senderAvatar;
            }

            let messageText = 'Sent you a message';
            if (messageData.is_file) {
              const fileType = messageData.file_type || 'file';
              if (fileType.startsWith('image')) messageText = 'Sent a photo';
              else if (fileType.startsWith('video')) messageText = 'Sent a video';
              else messageText = 'Sent a file';
            } else if (messageData.message && typeof messageData.message === 'string') {
              messageText = messageData.message.length > 60
                ? messageData.message.substring(0, 60) + '...'
                : messageData.message;
            }

            addPopupNotification({
              senderName: capitalizeEachFirstWord(senderName),
              avatar: senderAvatar,
              message: messageText,
              roomId: messageRoomId,
              messageId: messageData.id,
            });

            // Play sound — also respects per-room sound pref
            const soundPrefs = (() => { try { return JSON.parse(localStorage.getItem(`chatPref_${messageRoomId}`)) || {}; } catch { return {}; } })();
            if (soundPrefs.sound !== false) notificationSound.play('message');
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  React.useEffect(() => {
    if (!userData?.id) return;
    socket
      .off(SOCKET_EVENTS.SEARCH_DATA_RESULT)
      .on(SOCKET_EVENTS.SEARCH_DATA_RESULT, (result) => {
        dispatch({
          type: FILTER_CHATS,
          payload: {
            filteredChats: {
              success: true,
              direct_chats: result.direct_chats || {},
              group_chats: result.group_chats || {},
              new_users: result.new_users || [],
            },
            searchInProgress: false,
          },
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  // Single stable USER_TYPING listener — lives in AppContext so it never goes stale
  // regardless of block/unblock or chat switches.
  React.useEffect(() => {
    if (!Object.keys(userData).length) return;
    const handler = ({ senderId, roomId: typingRoomId, isTyping }) => {
      setTypingByRoom(prev => {
        const room = prev[typingRoomId] || {};
        if (isTyping) return { ...prev, [typingRoomId]: { ...room, [senderId]: true } };
        const next = { ...room };
        delete next[senderId];
        return { ...prev, [typingRoomId]: next };
      });
      // Auto-clear after 4 s in case the "stop" event never arrives (e.g. tab close)
      if (isTyping) {
        setTimeout(() => {
          setTypingByRoom(prev => {
            const room = { ...(prev[typingRoomId] || {}) };
            delete room[senderId];
            return { ...prev, [typingRoomId]: room };
          });
        }, 4000);
      }
    };
    socket.off(SOCKET_EVENTS.USER_TYPING).on(SOCKET_EVENTS.USER_TYPING, handler);
    return () => { socket.off(SOCKET_EVENTS.USER_TYPING, handler); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Listen for read receipt updates — when the other user has read our messages
  React.useEffect(() => {
    if (Object.keys(userData).length > 0) {
      socket
        .off(SOCKET_EVENTS.MESSAGES_READ_UPDATE)
        .on(SOCKET_EVENTS.MESSAGES_READ_UPDATE, ({ roomId }) => {
          dispatch({
            type: MARK_ROOM_MESSAGES_READ,
            payload: { roomId },
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Show a system notice when the other user changes disappearing-message setting
  React.useEffect(() => {
    if (!Object.keys(userData).length) return;
    socket
      .off(SOCKET_EVENTS.DISAPPEARING_UPDATED)
      .on(SOCKET_EVENTS.DISAPPEARING_UPDATED, ({ roomId: dRoomId, interval }) => {
        // Update the chat list entry so the timer badge appears/disappears in real-time.
        dispatch({ type: UPDATE_CHAT_PREF, payload: { roomId: dRoomId, disappearing_interval: interval } });
        // Notify ChatSettings (or any open listener) about the new shared interval.
        window.dispatchEvent(
          new CustomEvent("iota:disappearing_updated", { detail: { roomId: dRoomId, interval } })
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Per-message disappearing timers — mirrors how WhatsApp works.
  // Each message with a future disappears_at gets its own setTimeout so it clears
  // at the exact right moment.  The timerMapRef persists across renders so timers
  // are never duplicated.  This works offline too; no backend round-trip needed.
  const timerMapRef = React.useRef({});   // { messageId: timeoutId }

  const scheduleDisappearingTimers = React.useCallback((messages, roomId) => {
    const now = Date.now();
    (messages || []).forEach((msg) => {
      if (!msg.disappears_at || msg.is_kept || timerMapRef.current[msg.id]) return;
      const expiryMs = new Date(msg.disappears_at).getTime();
      // Guard: skip messages whose disappears_at cannot be parsed to a valid future timestamp.
      // NaN would cause setTimeout(fn, NaN) which fires immediately and wipes the message.
      if (!isFinite(expiryMs)) return;
      const msLeft = expiryMs - now;
      if (msLeft <= 0) {
        // Already expired — remove immediately (e.g. loaded from DB after being offline)
        dispatch({ type: EXPIRE_MESSAGES, payload: { roomId, messageIds: [msg.id] } });
        return;
      }
      timerMapRef.current[msg.id] = setTimeout(() => {
        delete timerMapRef.current[msg.id];
        dispatch({ type: EXPIRE_MESSAGES, payload: { roomId, messageIds: [msg.id] } });
      }, msLeft);
    });
  }, [dispatch]);

  // Cancel a timer when a message is kept so it doesn't disappear after the user chose to keep it
  const cancelDisappearingTimer = React.useCallback((messageId) => {
    if (timerMapRef.current[messageId]) {
      clearTimeout(timerMapRef.current[messageId]);
      delete timerMapRef.current[messageId];
    }
  }, []);

  // Remove expired disappearing messages from state when server notifies us
  React.useEffect(() => {
    if (!Object.keys(userData).length) return;
    socket
      .off(SOCKET_EVENTS.MESSAGE_EXPIRED)
      .on(SOCKET_EVENTS.MESSAGE_EXPIRED, ({ roomId, messageIds }) => {
        dispatch({ type: EXPIRE_MESSAGES, payload: { roomId, messageIds } });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    if (Object.keys(userData).length > 0) {
      socket
        .off(SOCKET_EVENTS.CREATE_GROUP_ROOM_RESULT)
        .on(SOCKET_EVENTS.CREATE_GROUP_ROOM_RESULT, async (result) => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // Handle user activity updates (especially for premium membership changes)
    if (Object.keys(userData).length > 0) {
      socket
        .off(SOCKET_EVENTS.USER_ACTIVITY_UPDATE)
        .on(SOCKET_EVENTS.USER_ACTIVITY_UPDATE, async (data) => {
          console.log("Activity update received:", data);

          // Check if the activity is for the current user
          if (data.user_id?.toString() === userData.id?.toString()) {
            // If premium related, fetch updated membership info
            if (data.tag && data.tag.startsWith("premium_")) {
              try {
                // Get fresh membership data
                const membershipInfo = await getUserMembershipInfoApiCall();

                if (membershipInfo.success) {
                  // Update the user data with the new membership info
                  dispatch({
                    type: SET_USER_DATA,
                    payload: {
                      ...userData,
                      userMembershipInfo: membershipInfo.data,
                    },
                  });

                  // For certain premium status changes, update account_type as well
                  if (
                    data.tag === "premium_upgrade" ||
                    data.tag === "premium_renewal" ||
                    data.tag === "premium_reactivated"
                  ) {
                    dispatch({
                      type: UPDATE_USER_DATA,
                      payload: {
                        id: userData.id,
                        account_type: "premium",
                      },
                    });
                  } else if (data.tag === "premium_expired") {
                    dispatch({
                      type: UPDATE_USER_DATA,
                      payload: {
                        id: userData.id,
                        account_type: "basic",
                      },
                    });
                  }
                }
              } catch (error) {
                console.error(
                  "Error updating membership info after activity update",
                  error,
                );
              }
            }
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, socket]);

  React.useEffect(() => {
    if (Object.keys(userData).length > 0) {
      socket
        .off(SOCKET_EVENTS.UPDATED_USER_DATA)
        .on(SOCKET_EVENTS.UPDATED_USER_DATA, async (data) => {
          // Only process if data is for current user
          if (data.id?.toString() === userData.id?.toString()) {
            // Handle premium membership specific updates
            if (
              data.hasOwnProperty("account_type") ||
              data.hasOwnProperty("auto_renew") ||
              data.hasOwnProperty("canceled") ||
              data.hasOwnProperty("is_premium_active")
            ) {
              // Fetch fresh membership info to ensure consistency
              try {
                const membershipInfo = await getUserMembershipInfoApiCall();
                if (membershipInfo.success) {
                  // Update user membership data
                  const updatedUserData = {
                    ...userData,
                    userMembershipInfo: membershipInfo.data,
                  };

                  // If account type is changing, update that too
                  if (data.account_type) {
                    updatedUserData.account_type = data.account_type;
                  }

                  // If coin balance is changing, update that too
                  if (data.available_coins) {
                    updatedUserData.available_coins = data.available_coins;
                  }

                  dispatch({
                    type: SET_USER_DATA,
                    payload: updatedUserData,
                  });

                  return; // Skip the normal UPDATE_USER_DATA dispatch as we handled it
                }
              } catch (error) {
                console.error(
                  "Error updating membership info after user data update",
                  error,
                );
              }
            }

            // For other user data updates, use the regular handler
            dispatch({
              type: UPDATE_USER_DATA,
              payload: data,
            });
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, socket]);

  /* This handles the socket event sent from the backend
  with the result of userId being online or not, after online status has already been determined
  More like user logs in when someone is on his/her profile */
  React.useEffect(() => {
    socket
      .off(SOCKET_EVENTS.UPDATED_ONLINE_STATUS)
      .on(SOCKET_EVENTS.UPDATED_ONLINE_STATUS, (data) => {
        const updatingUserId = data.user_id?.toString();
        const onlineStatus = data.online_status;
        const lastSeen = data.last_seen;
        const isOnlineNow = onlineStatus === ONLINE_STATUS.ONLINE ? 1 : 0;

        // Always dispatch both — reducer is safe to call unconditionally.
        // UPDATE_USER_ONLINE_STATUS patches activeChat.room_participants in place.
        // UPDATE_CHAT_ONLINE_STATUS patches state.chats AND state.activeChat together.
        dispatch({
          type: UPDATE_USER_ONLINE_STATUS,
          payload: { user_id: updatingUserId, online_status: onlineStatus, last_seen: lastSeen },
        });

        dispatch({
          type: UPDATE_CHAT_ONLINE_STATUS,
          payload: {
            userId: updatingUserId,
            is_online: isOnlineNow,
            last_seen: lastSeen,
          },
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wire up the response to USER_ONLINE_STATUS_REQUEST — previously the backend replied
  // but no handler existed so the payload was silently dropped.
  React.useEffect(() => {
    socket
      .off(SOCKET_EVENTS.USER_ONLINE_STATUS_RESULT)
      .on(SOCKET_EVENTS.USER_ONLINE_STATUS_RESULT, ({ user_id, online_status, last_seen }) => {
        const isOnlineNow = online_status === ONLINE_STATUS.ONLINE ? 1 : 0;
        dispatch({
          type: UPDATE_USER_ONLINE_STATUS,
          payload: { user_id: user_id?.toString(), online_status, last_seen },
        });
        dispatch({
          type: UPDATE_CHAT_ONLINE_STATUS,
          payload: { userId: user_id?.toString(), is_online: isOnlineNow, last_seen },
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep last_seen accurate while the tab is active and catch frozen-tab scenarios.
  const HEARTBEAT_INTERVAL_MS = 30_000;
  React.useEffect(() => {
    if (!userData?.id) return;
    const id = setInterval(() => {
      if (socket.connected) socket.emit(SOCKET_EVENTS.USER_HEARTBEAT);
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  // After a socket reconnect, re-request the active chat partner's online status.
  // Without this the other user can appear stuck as "offline" until the next navigation.
  React.useEffect(() => {
    if (!userData?.id) return;
    const handleReconnect = () => {
      const chat = activeChatIdRef.current
        ? appState.activeChat
        : null;
      if (chat?.type === CHAT_ROOMS.TYPES.SINGLE && chat?.room_participants) {
        const other = chat.room_participants.find(
          (p) => p.id?.toString() !== userData.id?.toString()
        );
        if (other?.id) socket.emit(SOCKET_EVENTS.USER_ONLINE_STATUS_REQUEST, other.id);
      }
    };
    socket.io.on("reconnect", handleReconnect);
    return () => socket.io.off("reconnect", handleReconnect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  React.useEffect(() => {
    //This will handle the service to getting room joining as an admin
    if (Object.keys(userData).length > 0) {
      socket
        .off(SOCKET_EVENTS.JOIN_ROOM_ADMIN_STATUS_REQUEST)
        .on(SOCKET_EVENTS.JOIN_ROOM_ADMIN_STATUS_REQUEST, async (result) => {
          const roomAdminId = result.room.admin_id;

          //Check if current logged in user is the admin of the room
          if (userData.id?.toString() === roomAdminId?.toString()) {
            //Check if room id and user id, is already in admin waiting list
            const requestExists = waitingRoomParticipantRequests.some((obj) => {
              // Compare the specific properties of the objects
              return (
                obj.otherUser.id?.toString() ===
                  result.otherUser.id?.toString() &&
                obj.room.id?.toString() === result.room.id?.toString() &&
                obj.room.name?.toString() === result.room.name?.toString() &&
                obj.room.admin_id?.toString() ===
                  result.room.admin_id?.toString()
              );
            });

            if (!requestExists) {
              dispatch({
                type: ADD_WAITING_ROOM_PARTICIPANT_REQUESTS,
                payload: result,
              });
            }
          }
          return true;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, waitingRoomParticipantRequests]);

  React.useEffect(() => {
    //This handles the socket event for admin to finalize user room participant request
    if (Object.keys(userData).length > 0) {
      socket
        .off(SOCKET_EVENTS.JOIN_ROOM_ADMIN_FINALE_RESULT)
        .on(SOCKET_EVENTS.JOIN_ROOM_ADMIN_FINALE_RESULT, async (data) => {
          const roomParticipantData = data.data;
          const roomParticipantMessage = data.message;
          const roomParticipantStatus = data.status;
          const joinRoomParticipantUserId = roomParticipantData?.user_id;
          const joinRoomParticipantRoomId = roomParticipantData?.room_id;

          // This event is sent either:
          //   (a) directly to this user — user_id matches current user
          //   (b) as a lobby broadcast (admin went offline) — user_id is null, but we
          //       only receive it because we're in the admin's lobby room, so it IS for us
          const isForCurrentUser =
            !joinRoomParticipantUserId ||
            userData.id?.toString() === joinRoomParticipantUserId?.toString();

          if (!isForCurrentUser) return true;

          if (CHAT_ROOMS.JOIN_REQUEST.REJECTED === roomParticipantStatus) {
            showSnackbar(roomParticipantMessage, SNACKBAR_VARIANT.ERROR);
            return history.push(ROUTES.dashboard);
          }

          showSnackbar(roomParticipantMessage, SNACKBAR_VARIANT.SUCCESS);
          return loadANewPage(`${ROUTES.rooms}/${joinRoomParticipantRoomId}`);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle the update of user ban status in other room participant active chat from socket service
    socket
      .off(SOCKET_EVENTS.BAN_USER_IN_GROUP_ROOM_STATUS_RESULT)
      .on(SOCKET_EVENTS.BAN_USER_IN_GROUP_ROOM_STATUS_RESULT, async (data) => {
        if (Object.keys(userData).length > 0) {
          const updatingRoomId = data.room_id;

          //Check if active is the room thats being updated
          if (updatingRoomId?.toString() === activeChat?.id?.toString()) {
            dispatch({
              type: UPDATE_USER_BAN_STATUS_IN_ACTIVE_ROOM,
              payload: data,
            });
          }
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // When admin reconnects after being offline, clear any requests that were auto-cancelled during their absence
    socket
      .off(SOCKET_EVENTS.JOIN_REQUESTS_EXPIRED)
      .on(SOCKET_EVENTS.JOIN_REQUESTS_EXPIRED, ({ cancelledRequests }) => {
        if (cancelledRequests?.length > 0) {
          dispatch({ type: CLEAR_EXPIRED_JOIN_REQUESTS, payload: cancelledRequests });
        }
      });

    // When a waiting user leaves the page or disconnects, remove their request from admin's list
    socket
      .off(SOCKET_EVENTS.JOIN_REQUEST_CANCELLED_BY_USER)
      .on(SOCKET_EVENTS.JOIN_REQUEST_CANCELLED_BY_USER, ({ userId, roomId }) => {
        dispatch({
          type: CLEAR_EXPIRED_JOIN_REQUESTS,
          payload: [{ userId, roomId }],
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // This will handle the update of user ban status in other room participant active chat from socket service
    socket
      .off(SOCKET_EVENTS.BAN_USER_TOOLBOX_IN_GROUP_ROOM_STATUS_RESULT)
      .on(
        SOCKET_EVENTS.BAN_USER_TOOLBOX_IN_GROUP_ROOM_STATUS_RESULT,
        async (data) => {
          if (Object.keys(userData).length > 0) {
            const updatingRoomId = data.room_id;
            const updatingRoomName = data.room_name;
            const updatingUserId = data.user_id;

            //Check if active is the room thats being updated
            if (updatingRoomId?.toString() === activeChat?.id?.toString()) {
              if (updatingUserId?.toString() === userData?.id?.toString()) {
                showSnackbar(
                  MESSAGE.TOOLBOX_STATUS_UPDATED(updatingRoomName),
                  SNACKBAR_VARIANT.DEFAULT,
                );
              }

              dispatch({
                type: UPDATE_USER_TOOLBOX_BAN_STATUS_IN_ACTIVE_ROOM,
                payload: data,
              });
            }
          }
        },
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle the update of user moderatorship status in other room participant active chat from socket service
    socket
      .off(SOCKET_EVENTS.USER_MODERATORSHIP_IN_GROUP_ROOM_STATUS_RESULT)
      .on(
        SOCKET_EVENTS.USER_MODERATORSHIP_IN_GROUP_ROOM_STATUS_RESULT,
        async (data) => {
          if (Object.keys(userData).length > 0) {
            const updatingRoomId = data.room_id;

            //Check if active is the room thats being updated
            if (updatingRoomId?.toString() === activeChat?.id?.toString()) {
              dispatch({
                type: UPDATE_USER_MODERATORSHIP_STATUS_IN_ACTIVE_ROOM,
                payload: data,
              });
            }
          }
        },
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle the update of user kick off vote count in other room participant active chat from socket service
    socket
      .off(SOCKET_EVENTS.KICK_USER_OUT_OF_GROUP_ROOM_STATUS_RESULT)
      .on(
        SOCKET_EVENTS.KICK_USER_OUT_OF_GROUP_ROOM_STATUS_RESULT,
        async (data) => {
          if (Object.keys(userData).length > 0) {
            const updatingRoomId = data.room_id;
            const kickedOutUserId = data.user_id;
            const updatedKickedOutCount = data.kickout_count;

            //Check if active is the room thats being updated
            if (updatingRoomId?.toString() === activeChat?.id?.toString()) {
              dispatch({
                type: UPDATE_USER_KICK_OUT_VOTE_IN_ACTIVE_ROOM,
                payload: data,
              });
            }

            /*Check if kicked out user is current logged in user
            Check if user's kick off count is upto 7 which is the max before being kicked out totally */
            if (kickedOutUserId?.toString() === userData.id?.toString()) {
              if (updatedKickedOutCount >= 7) {
                showSnackbar(MESSAGE.KICKED_OFF_CHAT, SNACKBAR_VARIANT.ERROR);
                //Redirect user to dashboard page
                return loadANewPage(ROUTES.dashboard);
              } else if (updatedKickedOutCount < 7) {
                showSnackbar(MESSAGE.KICKED_IN_CHAT, SNACKBAR_VARIANT.SUCCESS);
              }
            }
          }
        },
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle the update of the new room participants in active chat from socket service
    socket
      .off(SOCKET_EVENTS.UPDATED_ROOM_PARTICIPANT)
      .on(SOCKET_EVENTS.UPDATED_ROOM_PARTICIPANT, async (data) => {
        if (Object.keys(userData).length > 0) {
          const updatingRoomId = data.room_id;

          //Check if active is the room thats being updated
          if (
            updatingRoomId?.toString() === activeChat?.id?.toString() &&
            userData.id?.toString() !== data.id?.toString()
          ) {
            //Remove update room participant data
            delete data.room_id;

            dispatch({
              type: UPDATE_NEW_ROOM_PARTICIPANT_IN_ACTIVE_ROOM,
              payload: data,
            });
          }
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle the update of the editing of chat message from socket service
    socket
      .off(SOCKET_EVENTS.MESSAGE_EDITED_RESULT)
      .on(SOCKET_EVENTS.MESSAGE_EDITED_RESULT, async (data) => {
        if (Object.keys(userData).length > 0) {
          dispatch({
            type: UPDATE_EDITED_CHAT_MESSAGE,
            payload: data,
          });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle the update of the deletion of chat message from socket service
    socket
      .off(SOCKET_EVENTS.MESSAGE_DELETED_RESULT)
      .on(SOCKET_EVENTS.MESSAGE_DELETED_RESULT, async (data) => {
        if (Object.keys(userData).length > 0) {
          dispatch({
            type: UPDATE_DELETED_CHAT_MESSAGE,
            payload: data,
          });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle the update of the interaction on chat message from socket service
    socket
      .off(SOCKET_EVENTS.MESSAGE_INTERACTION_RESULT)
      .on(SOCKET_EVENTS.MESSAGE_INTERACTION_RESULT, async (data) => {
        if (Object.keys(userData).length > 0) {
          dispatch({
            type: UPDATE_MESSAGE_INTERACTION_IN_ACTIVE_ROOM,
            payload: data,
          });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle the update of the tipping on chat message from socket service
    socket
      .off(SOCKET_EVENTS.MESSAGE_TIPPING_RESULT)
      .on(SOCKET_EVENTS.MESSAGE_TIPPING_RESULT, async (data) => {
        if (Object.keys(userData).length > 0) {
          dispatch({
            type: UPDATE_MESSAGE_TIPPING_IN_ACTIVE_ROOM,
            payload: { isSocket: true, ...data },
          });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle the update of the room team score on chat message from socket service
    socket
      .off(SOCKET_EVENTS.ROOM_TEAM_SCORE_RESULT)
      .on(SOCKET_EVENTS.ROOM_TEAM_SCORE_RESULT, async (data) => {
        if (Object.keys(userData).length > 0) {
          dispatch({
            type: UPDATE_ROOM_TEAM_SCORE_IN_ACTIVE_ROOM,
            payload: data,
          });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle the update of the user data from socket service
    socket
      .off(SOCKET_EVENTS.UPDATED_USER_DATA)
      .on(SOCKET_EVENTS.UPDATED_USER_DATA, async (data) => {
        if (Object.keys(userData).length > 0) {
          dispatch({
            type: UPDATE_USER_DATA,
            payload: data,
          });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle the update of the auto score in rooms from socket service
    socket
      .off(SOCKET_EVENTS.AUTO_UPDATED_ROOM_TEAM_SCORE)
      .on(SOCKET_EVENTS.AUTO_UPDATED_ROOM_TEAM_SCORE, async (data) => {
        if (Object.keys(userData).length > 0) {
          dispatch({
            type: UPDATE_ROOM_TEAM_SCORE_IN_ACTIVE_ROOM,
            payload: data,
          });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle the update of the active chat room expiration time from socket service
    socket
      .off(SOCKET_EVENTS.EXTENDED_ROOM_EXPIRATION_TIME)
      .on(SOCKET_EVENTS.EXTENDED_ROOM_EXPIRATION_TIME, async (data) => {
        if (
          Object.keys(userData).length > 0 &&
          Object.keys(activeChat).length > 0
        ) {
          const updatingActiveChatData = { ...data };
          const actingUserId = updatingActiveChatData.acting_user_id;

          const updatingActiveRoomId =
            updatingActiveChatData.room_id?.toString();

          //Check if active room is the said room
          if (
            updatingActiveRoomId !== activeChat.id?.toString() ||
            actingUserId?.toString() === userData?.id?.toString()
          ) {
            return false;
          }

          const message = updatingActiveChatData.message;
          delete updatingActiveChatData.message;

          dispatch({
            type: UPDATE_ACTIVE_CHAT_DATA,
            payload: updatingActiveChatData,
          });

          showSnackbar(message);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // This will handle any error from socket service
    socket
      .off(SOCKET_EVENTS.ERROR_MESSAGE)
      .on(SOCKET_EVENTS.ERROR_MESSAGE, async (data) => {
        // Backend now sends { message, tempId } for send failures — fail the specific optimistic immediately
        if (data && typeof data === "object" && data.tempId) {
          const failedTempId = data.tempId;
          const timer = pendingTimersRef.current[failedTempId];
          if (timer) {
            clearTimeout(timer);
            delete pendingTimersRef.current[failedTempId];
          }
          dispatch({ type: FAIL_OPTIMISTIC_MESSAGE, payload: { roomId: activeChatIdRef.current, tempId: failedTempId } });
          return showSnackbar("Message failed to send. Please try again.", SNACKBAR_VARIANT.ERROR);
        }

        if (typeof data === "string") {
          const pendingIds = Object.keys(pendingTimersRef.current);
          if (pendingIds.length > 0) {
            const latestTempId = pendingIds[pendingIds.length - 1];
            clearTimeout(pendingTimersRef.current[latestTempId]);
            delete pendingTimersRef.current[latestTempId];
            dispatch({ type: FAIL_OPTIMISTIC_MESSAGE, payload: { roomId: activeChatIdRef.current, tempId: latestTempId } });
          }
          return showSnackbar(data, SNACKBAR_VARIANT.ERROR);
        }

        if (userData.id?.toString() === data.user_id?.toString()) {
          showSnackbar(data.message, SNACKBAR_VARIANT.ERROR);
          return history.push(ROUTES.dashboard);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Block-period messages — store locally, never delivered to receiver
  React.useEffect(() => {
    if (!Object.keys(userData).length) return;
    socket
      .off(SOCKET_EVENTS.MESSAGE_BLOCKED)
      .on(SOCKET_EVENTS.MESSAGE_BLOCKED, ({ message, fileType, caption, roomId, replyToId, timestamp, tempId: blockedTempId }) => {
        // Cancel the pending failure timer for this optimistic message
        if (blockedTempId) {
          const timer = pendingTimersRef.current[blockedTempId];
          if (timer) {
            clearTimeout(timer);
            delete pendingTimersRef.current[blockedTempId];
          }
        }
        const blockedMsg = {
          id: `blocked_${timestamp || Date.now()}_${Math.random().toString(36).slice(2)}`,
          message,
          file_type: fileType || MESSAGE_FILE_TYPES.TEXT,
          is_file: fileType && fileType !== MESSAGE_FILE_TYPES.TEXT ? 1 : 0,
          caption: caption ?? null,
          reply_to_id: replyToId ?? null,
          is_blocked_period: true,
          sender_id: userData.id,
          created_at: new Date(timestamp || Date.now()).toISOString(),
          room_id: roomId,
        };
        dispatch({ type: ADD_BLOCKED_PERIOD_MESSAGE, payload: { roomId, message: blockedMsg, tempId: blockedTempId } });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Keep/unkeep notices — add system message for both parties
  React.useEffect(() => {
    if (!Object.keys(userData).length) return;
    socket
      .off(SOCKET_EVENTS.MESSAGE_KEPT_NOTICE)
      .on(SOCKET_EVENTS.MESSAGE_KEPT_NOTICE, ({ roomId, messageId, username, unkeep, isSelf }) => {
        const actor = isSelf ? "You" : username;
        const text = unkeep
          ? `${actor} unkept a message`
          : `${actor} kept a message`;
        dispatch({ type: ADD_SYSTEM_NOTICE, payload: { roomId, text } });
        if (!unkeep) {
          dispatch({ type: KEEP_MESSAGE, payload: { roomId, messageId } });
        } else {
          dispatch({ type: UNKEEP_MESSAGE, payload: { roomId, messageId } });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Tip notice — in-chat system message for tipper and message owner
  React.useEffect(() => {
    if (!Object.keys(userData).length) return;
    socket
      .off(SOCKET_EVENTS.TIP_NOTICE)
      .on(SOCKET_EVENTS.TIP_NOTICE, ({ roomId, messageId, tipperUsername, coinValue, isSelf }) => {
        const actor = isSelf ? "You" : tipperUsername;
        const text = `${actor} tipped ${coinValue ?? 1} coin${coinValue !== 1 ? "s" : ""} on a message`;
        dispatch({ type: ADD_SYSTEM_NOTICE, payload: { roomId, text } });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  React.useEffect(() => {
    // Handle bookmark events in real-time
    if (Object.keys(userData).length > 0) {
      socket
        .off(SOCKET_EVENTS.ROOM_BOOKMARKED)
        .on(SOCKET_EVENTS.ROOM_BOOKMARKED, async (data) => {
          // Trigger custom event for components to listen to
          window.dispatchEvent(
            new CustomEvent(CUSTOM_EVENTS.ROOM_BOOKMARK_CHANGED, {
              detail: { roomId: data.room_id, bookmarked: true },
            })
          );
        });

      socket
        .off(SOCKET_EVENTS.ROOM_UNBOOKMARKED)
        .on(SOCKET_EVENTS.ROOM_UNBOOKMARKED, async (data) => {
          // Trigger custom event for components to listen to
          window.dispatchEvent(
            new CustomEvent(CUSTOM_EVENTS.ROOM_BOOKMARK_CHANGED, {
              detail: { roomId: data.room_id, bookmarked: false },
            })
          );
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, socket]);

  const setUserData = (data) => {
    dispatch({
      type: SET_USER_DATA,
      payload: data,
    });

    //Send login state
    socket.emit(SOCKET_EVENTS.LOGIN_USER);
  };

  const toggleRoomToolbox = () => {
    if (Object.keys(activeChat).length > 0) {
      if (roomToolbox.currentUserToolboxEnabled) {
        dispatch({
          type: ACTIONS.TOGGLE_ROOM_TOOLBOX,
        });
      } else {
        return showSnackbar(
          MESSAGE.ROOM_TOOLBOX_NOT_ACTIVE,
          SNACKBAR_VARIANT.DEFAULT,
        );
      }
    } else {
      return showSnackbar(MESSAGE.ROOM_NOT_ACTIVE, SNACKBAR_VARIANT.DEFAULT);
    }
  };

  const setCurrentUserRoomToolboxEnability = (data) => {
    dispatch({
      type: ACTIONS.SET_ROOM_TOOLBOX_ENABILITY,
      payload: data,
    });
  };

  const errorMessageHandler = (message) => {
    return message && typeof message === "string"
      ? message
      : MESSAGE.UNEXPECTED_ERROR;
  };

  const headers = {
    "x-access-token": accessToken,
    "Content-Type": "application/json",
  };

  const loginUserApiCall = async (options) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.post("/users/auth/login", options);
      setAuthTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
      dataLoadinHandler(false);
      showSnackbar(MESSAGE.LOGIN_SUCCESSFUL, SNACKBAR_VARIANT.SUCCESS);
      //Redirect user to dashboard page
      return loadANewPage(ROUTES.dashboard);
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const registerUserApiCall = async (options) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.post("/users/auth/signup", options);
      setAuthTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
      dataLoadinHandler(false);
      showSnackbar(MESSAGE.REGISTRATION_SUCCESSFUL, SNACKBAR_VARIANT.SUCCESS);
      return loadANewPage(ROUTES.dashboard);
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const forgotPasswordApiCall = async (options) => {
    try {
      dataLoadinHandler(true);
      await axios.post("/users/auth/forgot-password", options);
      dataLoadinHandler(false);
      return showSnackbar(
        MESSAGE.FORGOT_PASSWORD_SUCCESSFUL,
        SNACKBAR_VARIANT.SUCCESS,
      );
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const resetPasswordApiCall = async (options, userToken) => {
    try {
      dataLoadinHandler(true);
      axios.defaults.headers.post["x-access-token"] = userToken;
      await axios.post("/users/auth/reset-password", options);
      dataLoadinHandler(false);
      showSnackbar(MESSAGE.RESET_PASSWORD_SUCCESSFUL, SNACKBAR_VARIANT.SUCCESS);
      return history.push(REACT_APP_BEFORE_LOGIN_REDIRECT_URL);
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const VerifyAccountApiCall = async (userToken) => {
    try {
      axios.defaults.headers.post["x-access-token"] = userToken;
      await axios.post("/users/auth/verify-account");

      showSnackbar(
        MESSAGE.ACCOUNT_VERIFICATION_SUCCESSFUL,
        SNACKBAR_VARIANT.SUCCESS,
      );
      return history.push(REACT_APP_BEFORE_LOGIN_REDIRECT_URL);
    } catch (err) {
      history.push(REACT_APP_BEFORE_LOGIN_REDIRECT_URL);
      return showSnackbar(
        MESSAGE.ACCOUNT_VERIFICATION_UNSUCCESSFUL,
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  const refreshTokenApiCall = async () => {
    // If a refresh is already in-flight, wait for it instead of firing another request.
    // This prevents the 55-component race condition where every useApp() caller tries
    // to refresh the token simultaneously, causing all-but-one to fail and wipe the token.
    if (_refreshTokenPromise) {
      return _refreshTokenPromise;
    }

    const doRefresh = async () => {
      try {
        const { data } = await axios.get(
          `/users/auth/refresh-token?token=${refreshToken}`,
        );
        //Update access token if refresh token is still active
        setAuthTokens({
          accessToken: data.accessToken,
          refreshToken: refreshToken,
        });

        const user = await axios.get("/users/account", {
          headers: { ...headers, "x-access-token": data.accessToken },
        });
        //update user data for easy access across the app
        dataLoadinHandler(false);
        return setUserData(user.data.data);
      } catch (err) {
        dataLoadinHandler(false);

        //Remove expired tokens and return user to login page
        setAuthTokens({
          accessToken: null,
          refreshToken: null,
        });
        _accountFetchInitiated = false; // allow retry after re-login
        _initialDataFetchedForUserId = null;
        // showSnackbar(MESSAGE.AUTH_UNSUCCESSFUL);
        return history.push(REACT_APP_BEFORE_LOGIN_REDIRECT_URL);
      } finally {
        _refreshTokenPromise = null;
      }
    };

    _refreshTokenPromise = doRefresh();
    return _refreshTokenPromise;
  };

  const GetAccountApiCall = async () => {
    dataLoadinHandler(true);
    try {
      const user = await axios.get("/users/account", {
        headers,
      });
      dataLoadinHandler(false);

      //Send request to fetch chats
      socket.emit(SOCKET_EVENTS.LOAD_USER_CHATS_REQUEST);
      //update user data for easy access across the app
      return setUserData(user.data.data);
    } catch (err) {
      dataLoadinHandler(false);
      // Only attempt token refresh for real auth failures (401/403).
      // Network errors or 5xx server errors should NOT log the user out.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        refreshTokenApiCall();
      } else {
        console.error("[GetAccountApiCall] Non-auth error, skipping refresh:", err.message);
      }
    }
  };

  const GetAllRoomsApiCall = async () => {
    try {
      const theResult = await axios.get("/chat-rooms/rooms", {
        headers,
      });

      return dispatch({
        type: SET_ALL_GROUP_CHATS,
        payload: theResult.data.data,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        refreshTokenApiCall();
      }
    }
  };

  const logoutUser = async () => {
    resetApp();
    socket.emit(SOCKET_EVENTS.LOGOUT_USER, userData.id);
    return showSnackbar(MESSAGE.LOGOUT_SUCCESSFUL);
  };

  /**
   * Search for users and conversations
   * The backend does ALL the heavy lifting - frontend just displays results
   * Searches: users (by username/email), existing conversations, and group chats
   */
  const searchThroughChats = async (search) => {
    if (!search || !search.trim()) {
      dispatch({
        type: FILTER_CHATS,
        payload: {
          filteredChats: { success: false, direct_chats: {}, group_chats: {}, new_users: [] },
          searchInProgress: false,
        },
      });
      return;
    }

    // Set loading state
    dispatch({
      type: FILTER_CHATS,
      payload: {
        filteredChats: { success: true, direct_chats: {}, group_chats: {}, new_users: [] },
        searchInProgress: true,
      },
    });

    // Send search request to backend - results arrive via SEARCH_DATA_RESULT socket event
    socket.emit(SOCKET_EVENTS.SEARCH_DATA_REQUEST, search.trim());
  };

  //This handles the switch of current chat on the right side of the screen
  const switchActiveChat = async (chatData) => {
    //Check if active chat is empty or not the already selected one
    if (
      activeChat?.id?.toString() !== chatData?.id?.toString() ||
      !Object.keys(activeChat).length > 0
    ) {
      dispatch({
        type: SET_ACTIVE_CHAT,
        payload: chatData,
      });
      //Send request/socket event to know if user is online, only if chatData is of a single user
      if (chatData?.type === CHAT_ROOMS.TYPES.SINGLE) {
        const otherUserForDirectChat = chatData?.room_participants?.find(
          (participant) => participant.is_admin === 0,
        );
        const otherUserId = otherUserForDirectChat?.id;
        if (otherUserId) {
          socket.emit(SOCKET_EVENTS.USER_ONLINE_STATUS_REQUEST, otherUserId);
        }
      }
    }
  };

  // Optimistic message send — shows message immediately, confirms/fails via socket response
  const OPTIMISTIC_TIMEOUT_MS = 30000; // 30 seconds before showing retry button

  const sendMessageToChat = (data) => {
    const { message, roomId, fileType, replyToId, caption, tempId: existingTempId, localPreviewUrl, is_forwarded } = data;
    if (!message) return null;

    const tempId = existingTempId || `opt_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    // Only add an optimistic message when there isn't one pre-created externally (e.g. images)
    if (!existingTempId) {
      const optimisticMsg = {
        id: tempId,
        tempId,
        message,
        file_type: fileType || MESSAGE_FILE_TYPES.TEXT,
        is_file: fileType && fileType !== MESSAGE_FILE_TYPES.TEXT ? 1 : 0,
        caption: caption || null,
        reply_to_id: replyToId || null,
        sender_id: userData.id,
        room_id: roomId,
        is_optimistic: true,
        is_pending: true,
        is_failed: false,
        is_read: 0,
        is_forwarded: !!is_forwarded,
        local_preview_url: localPreviewUrl || null,
        created_at: new Date().toISOString(),
        local_sent_at: Date.now(),
      };
      dispatch({ type: ADD_OPTIMISTIC_MESSAGE, payload: { roomId, message: optimisticMsg } });
    }

    socket.emit(SOCKET_EVENTS.SEND_MESSAGE, { ...data, tempId });

    // Start failure timer
    const timer = setTimeout(() => {
      delete pendingTimersRef.current[tempId];
      dispatch({ type: FAIL_OPTIMISTIC_MESSAGE, payload: { roomId, tempId } });
    }, OPTIMISTIC_TIMEOUT_MS);
    pendingTimersRef.current[tempId] = timer;

    return tempId;
  };

  //This handles joinning a room with the help of socket events
  const joinARoom = async (data) => {
    socket.emit(SOCKET_EVENTS.JOIN_A_ROOM_REQUEST, data);
  };

  //This handles joinning a room with the help of api
  const joinARoomApiCall = async (data) => {
    dataLoadinHandler(true);
    try {
      const returnedData = await axios.post("/chat-rooms/join-room", data, {
        headers,
      });
      dataLoadinHandler(false);
      const dataObj = returnedData.data.data;
      return history.push(`${ROUTES.rooms}/${dataObj.id}`);
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  //This handles creation of a room with the help of api
  const createARoomApiCall = async (data) => {
    const {
      name,
      passcode,
      join_fee,
      watch_fee,
      duration_in_mins,
      team_limit,
      room_teams,
    } = data;
    if (
      !name ||
      !passcode ||
      !join_fee ||
      !watch_fee ||
      !duration_in_mins ||
      !team_limit ||
      !room_teams.length > 0
    ) {
      return showSnackbar(MESSAGE.MISSING_FIELD, SNACKBAR_VARIANT.ERROR);
    }

    //Check if room team name and description is available
    for await (const team of room_teams) {
      if (!team.name || !team.description) {
        return showSnackbar(MESSAGE.MISSING_FIELD, SNACKBAR_VARIANT.ERROR);
      } else if (team.name?.toLowerCase().trim() === "admin") {
        return showSnackbar(
          MESSAGE.ROOM_TEAMN_NOT_AVAILABLE,
          SNACKBAR_VARIANT.ERROR,
        );
      }
    }

    //Check if room team names are different from each other
    const teamNames = await room_teams.map((team) => team.name);
    const uniqueTeamNames = new Set(teamNames);
    if (uniqueTeamNames.size !== teamNames.length) {
      return showSnackbar(MESSAGE.DUPLICATE_TEAM_NAMES, SNACKBAR_VARIANT.ERROR);
    }

    try {
      const returnedData = await axios.post("/chat-rooms/create-room", data, {
        headers,
      });
      const dataObj = returnedData.data.data;
      return history.push(`${ROUTES.rooms}/${dataObj.id}`);
    } catch (err) {
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  //This handles creating a room with the help of socket events
  const createARoom = async (data) => {
    socket.emit(SOCKET_EVENTS.CREATE_GROUP_ROOM_REQUEST, data);
  };

  //This handles fetching of a room data with the help of api
  const fetchARoomByIdApiCall = async (id, isUserOnSpectatorMode) => {
    const url = isUserOnSpectatorMode
      ? `/chat-rooms/rooms/${id}?spectator_mode=true`
      : `/chat-rooms/rooms/${id}`;
    dataLoadinHandler(true);
    try {
      const returnedData = await axios.get(url, {
        headers,
      });
      const dataObj = await returnedData.data.data;
      const status = await Number(returnedData.status);
      dataLoadinHandler(false);

      //Check if status is 200, which means backend returned the room details successfully
      if (status === 200) {
        switchActiveChat(dataObj);
      }
      return status === 200 ? { status } : { status, data: dataObj };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      history.push(`${ROUTES.dashboard}`);
      return { status: false };
    }
  };

  //This handles the approve or rejection of a user's room join request by the admin when online
  const adminCheckInUserIntoRoom = (data) => {
    //This will help remove the participant from waiting list request
    dispatch({
      type: UPDATE_WAITING_ROOM_PARTICIPANT_REQUESTS,
      payload: { id: data.id },
    });
    //Remove id, because its not wanted in the backend again
    delete data.id;
    socket.emit(SOCKET_EVENTS.JOIN_ROOM_ADMIN_STATUS_RESULT, data);
  };

  // User cancels their own pending join request (called on page leave / unmount)
  const cancelJoinRequest = () => {
    socket.emit(SOCKET_EVENTS.JOIN_REQUEST_CANCELLED_BY_USER);
  };

  //This handles the expiration of a room after room countdown
  const updateExpiredActiveChat = (roomId) => {
    //This will help remove the backend know room is expired in real time
    socket.emit(SOCKET_EVENTS.UPDATE_EXPIRED_ROOM, roomId);

    dispatch({
      type: UPDATE_EXPIRED_ACTIVE_CHAT,
      payload: { room_id: roomId },
    });
  };

  //This handles updating a user's room team with the help of api
  const updateARoomTeamByIdApiCall = async (data) => {
    if (!data && !Object.keys(data).length) {
      return showSnackbar(MESSAGE.CHOOSE_A_ROOM_TEAM, SNACKBAR_VARIANT.ERROR);
    }

    const { roomId, teamId } = data;
    dataLoadinHandler(true);
    try {
      const dataToPass = {
        room_team_id: teamId,
      };
      const { data } = await axios.patch(
        `/chat-rooms/rooms/${roomId}/team`,
        dataToPass,
        {
          headers,
        },
      );
      dataLoadinHandler(false);
      showSnackbar(data.message, SNACKBAR_VARIANT.SUCCESS);
      return loadANewPage(`${ROUTES.rooms}/${roomId}`);
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  //This handles updating a user's room team with the help of api
  const payRoomFeeForSpectatorModeByRoomIdApiCall = async ({ roomId }) => {
    try {
      const { data } = await axios.get(
        `/chat-rooms/rooms/${roomId}/pay-spectator-watch-fee`,
        {
          headers,
        },
      );
      showSnackbar(data.message, SNACKBAR_VARIANT.SUCCESS);
      setTimeout(() => {
        return loadANewPage(`${ROUTES.spectate}/${roomId}`);
      }, 3000);
    } catch (err) {
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  //This handles the banning and unbanning of a user in a room
  const adminUpdateUserBanStatusApiCall = async (
    dataToPass,
    roomModerators,
    roomId,
    isAdminBan,
  ) => {
    if (!isAdminBan) {
      //Add to temporal muted room participants in store
      dispatch({
        type: UPDATE_MUTED_ROOM_PARTICIPANT_IN_ACTIVE_ROOM,
        payload: dataToPass.participant_id,
      });
      return true;
    }

    //Check if current user is part of the room moderators
    const confirmCurrentUserAsRoomModerator = roomModerators.some(
      (participant) => participant.id?.toString() === userData.id?.toString(),
    );
    if (!confirmCurrentUserAsRoomModerator) {
      return showSnackbar(MESSAGE.PERMISSION, SNACKBAR_VARIANT.ERROR);
    }

    dataLoadinHandler(true);
    try {
      const { data } = await axios.patch(
        `/chat-rooms/rooms/${roomId}/ban-status`,
        dataToPass,
        {
          headers,
        },
      );
      dataLoadinHandler(false);
      showSnackbar(data.message, SNACKBAR_VARIANT.SUCCESS);

      const dataNeeded = data.data;
      const updatingRoomId = dataNeeded.room_id;

      if (updatingRoomId?.toString() === activeChat?.id?.toString()) {
        dispatch({
          type: UPDATE_USER_BAN_STATUS_IN_ACTIVE_ROOM,
          payload: data.data,
        });
      }
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  //This handles the banning and unbanning of a user toolbox in a room
  const adminUpdateUserToolboxBanStatusApiCall = async (
    dataToPass,
    roomModerators,
    roomId,
    isAdminBan,
  ) => {
    if (!isAdminBan) {
      return true;
    }

    //Check if current user is part of the room moderators
    const confirmCurrentUserAsRoomModerator = roomModerators.some(
      (participant) => participant.id?.toString() === userData.id?.toString(),
    );
    if (!confirmCurrentUserAsRoomModerator) {
      return showSnackbar(MESSAGE.PERMISSION, SNACKBAR_VARIANT.ERROR);
    }

    dataLoadinHandler(true);
    try {
      const { data } = await axios.patch(
        `/chat-rooms/rooms/${roomId}/ban-toolbox-status`,
        dataToPass,
        {
          headers,
        },
      );
      dataLoadinHandler(false);
      showSnackbar(data.message, SNACKBAR_VARIANT.SUCCESS);

      const dataNeeded = data.data;
      const updatingRoomId = dataNeeded.room_id;

      if (updatingRoomId?.toString() === activeChat?.id?.toString()) {
        dispatch({
          type: UPDATE_USER_TOOLBOX_BAN_STATUS_IN_ACTIVE_ROOM,
          payload: data.data,
        });
      }
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  //This handles the banning and unbanning of a user in a room
  const adminUpdateUserModeratorshipStatusApiCall = async (
    dataToPass,
    roomModerators,
    roomId,
  ) => {
    //Check if current user is part of the room moderators
    const confirmCurrentUserAsRoomMainModerator = roomModerators.some(
      (participant) =>
        participant.id?.toString() === userData.id?.toString() &&
        participant.is_admin?.toString() === "1",
    );
    if (!confirmCurrentUserAsRoomMainModerator) {
      return showSnackbar(MESSAGE.PERMISSION, SNACKBAR_VARIANT.ERROR);
    }

    dataLoadinHandler(true);
    try {
      const { data } = await axios.patch(
        `/chat-rooms/rooms/${roomId}/moderator-status`,
        dataToPass,
        {
          headers,
        },
      );
      dataLoadinHandler(false);
      showSnackbar(data.message, SNACKBAR_VARIANT.SUCCESS);

      const dataNeeded = data.data;
      const updatingRoomId = dataNeeded.room_id;

      if (updatingRoomId?.toString() === activeChat?.id?.toString()) {
        dispatch({
          type: UPDATE_USER_MODERATORSHIP_STATUS_IN_ACTIVE_ROOM,
          payload: data.data,
        });
      }
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  //This handles the kicking off and on of a user in a room
  const userKickOffParticipantVoteApiCall = async (dataToPass, roomId) => {
    dataLoadinHandler(true);
    try {
      const { data } = await axios.patch(
        `/chat-rooms/rooms/${roomId}/kick-out-vote`,
        dataToPass,
        {
          headers,
        },
      );
      dataLoadinHandler(false);
      showSnackbar(data.message, SNACKBAR_VARIANT.SUCCESS);

      const dataNeeded = data.data;
      const updatingRoomId = dataNeeded.room_id;

      if (updatingRoomId?.toString() === activeChat?.id?.toString()) {
        dispatch({
          type: UPDATE_USER_KICK_OUT_VOTE_IN_ACTIVE_ROOM,
          payload: data.data,
        });
      }
      return data.data;
    } catch (err) {
      dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  //This handles the editing of message in a room
  const editChatMessageApiCall = async (roomId, messageId, dataToPass) => {
    // Optimistic update — show the new text immediately for a smooth experience
    dispatch({
      type: UPDATE_EDITED_CHAT_MESSAGE,
      payload: { room_id: roomId, message_id: messageId, message: dataToPass.message, is_edited: true },
    });
    try {
      await axios.patch(
        `/chat-rooms/rooms/${roomId}/messages/${messageId}`,
        dataToPass,
        { headers },
      );
      // Success: the socket broadcast (MESSAGE_EDITED_RESULT) will update the other user
    } catch (err) {
      // Revert optimistic update on failure — restore original text
      showSnackbar(errorMessageHandler(err?.response?.data?.message), SNACKBAR_VARIANT.ERROR);
    }
  };

  //This handles the deleting of message in a room
  const deleteMessageForMeApiCall = async (roomId, messageId) => {
    try {
      await axios.delete(`/chat-rooms/rooms/${roomId}/messages/${messageId}/for-me`, { headers });
    } catch (err) {
      showSnackbar(errorMessageHandler(err?.response?.data?.message), SNACKBAR_VARIANT.ERROR);
    }
  };

  const deleteChatMessageApiCall = async (roomId, messageId) => {
    // dataLoadinHandler(true);
    try {
      const { data } = await axios.delete(
        `/chat-rooms/rooms/${roomId}/messages/${messageId}`,
        {
          headers,
        },
      );
      // dataLoadinHandler(false);
      // showSnackbar(data.message, SNACKBAR_VARIANT.SUCCESS);

      const dataNeeded = data.data;

      dispatch({
        type: UPDATE_DELETED_CHAT_MESSAGE,
        payload: dataNeeded,
      });
      return dataNeeded;
    } catch (err) {
      // dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  //This handles the message interaction of message in a room
  const updateChatMessageInteractionApiCall = async (
    roomId,
    messageId,
    type,
    emoji,
  ) => {
    try {
      const { data } = await axios.post(
        `/chat-rooms/rooms/${roomId}/messages/${messageId}/interactions`,
        { type, ...(emoji ? { emoji } : {}) },
        { headers },
      );

      const dataNeeded = data.data;

      dispatch({
        type: UPDATE_MESSAGE_INTERACTION_IN_ACTIVE_ROOM,
        payload: dataNeeded,
      });
      return dataNeeded;
    } catch (err) {
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  //This handles the message tipping of coin in a room
  const updateChatMessageTipApiCall = async (roomId, messageId) => {
    // Don't show full page loading for quick tipping action
    // dataLoadinHandler(true);
    try {
      const { data } = await axios.get(
        `/chat-rooms/rooms/${roomId}/messages/${messageId}/tip`,
        {
          headers,
        },
      );
      // dataLoadinHandler(false);
      // showSnackbar(data.message, SNACKBAR_VARIANT.SUCCESS);

      const dataNeeded = data.data;

      dispatch({
        type: UPDATE_MESSAGE_TIPPING_IN_ACTIVE_ROOM,
        payload: dataNeeded,
      });
      return dataNeeded;
    } catch (err) {
      // dataLoadinHandler(false);
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  //This handles pagination of rooms/conversations in the messages page with the help of api
  const fetchMoreRoomsAndMessagesApiCall = async () => {
    const currentPageFetchedForAllConversations = chatsPaginations.current_page;
    const nextPageToFetchForAllConversations = chatsPaginations.next_page;
    const pageSizeToFetchForAllConversations = chatsPaginations.page_size;

    // If there are no more data left based on the next page count
    if (!nextPageToFetchForAllConversations) {
      //Check if it was at the first page that no more messages were accounted for
      if (currentPageFetchedForAllConversations > 1) {
        return {
          success: false,
          reachedLastConversationAfterloadingMore: true,
          message: MESSAGE.NO_REMAINING_ROOM_CHATS,
        };
      }
      return { success: false, message: MESSAGE.NO_REMAINING_ROOM_CHATS };
    }

    try {
      const returnedData = await axios.get(
        `/chat-rooms/conversations?pageNo=${nextPageToFetchForAllConversations}&pageSize=${pageSizeToFetchForAllConversations}`,
        {
          headers,
        },
      );
      const dataObj = returnedData.data.data;

      dispatch({
        type: UPDATE_ALL_CHATS_LIST,
        payload: dataObj,
      });

      return { success: true };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  //This handles pagination of room/conversation message in the active room page with the help of api
  const fetchMoreRoomMessagesApiCall = async () => {
    const activeRoomId = activeChat?.id;
    const activeChatPaginations = activeChat?.pagination;

    const currentPageFetchedForAllConversationMesssage =
      activeChatPaginations.current_page;
    const nextPageToFetchForAllConversationMesssage =
      activeChatPaginations.next_page;
    // Use 20 messages per load for pagination (infinite scroll)
    const pageSizeToFetchForAllConversationMesssage = 20;

    // If there are no more data left based on the next page count
    if (!nextPageToFetchForAllConversationMesssage) {
      //Check if it was at the first page that no more messages were accounted for
      if (currentPageFetchedForAllConversationMesssage > 1) {
        return {
          success: false,
          reachedLastMessageAfterloadingMore: true,
          message: MESSAGE.NO_REMAINING_ROOM_MESSAGES,
        };
      }
      return { success: false, message: MESSAGE.NO_REMAINING_ROOM_MESSAGES };
    }

    try {
      const returnedData = await axios.get(
        `/chat-rooms/conversations/${activeRoomId}?pageNo=${nextPageToFetchForAllConversationMesssage}&pageSize=${pageSizeToFetchForAllConversationMesssage}`,
        {
          headers,
        },
      );
      const dataObj = returnedData.data.data;

      dispatch({
        type: UPDATE_ACTIVE_CHAT_LIST,
        payload: dataObj,
      });
      // Schedule timers for any disappearing messages in the newly loaded page
      scheduleDisappearingTimers(dataObj.messages, activeChat?.id);

      return { success: true };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  //This handles searching room messages (including paginated ones not loaded in UI)
  const searchRoomMessagesApiCall = async (searchQuery, pageNo = 1, pageSize = 20) => {
    const activeRoomId = activeChat?.id;

    if (!activeRoomId || !searchQuery || searchQuery.trim() === '') {
      return { success: false, message: 'Invalid search parameters' };
    }

    try {
      const returnedData = await axios.get(
        `/chat-rooms/conversations/${activeRoomId}/search?search=${encodeURIComponent(searchQuery)}&pageNo=${pageNo}&pageSize=${pageSize}`,
        {
          headers,
        },
      );
      const dataObj = returnedData.data.data;

      return { success: true, data: dataObj };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  //This handles fetching of a user data with the help of api
  const fetchAUserByIdApiCall = async (id) => {
    try {
      const returnedData = await axios.get(`/users?search=${id}`, {
        headers,
      });
      const dataObj = await returnedData.data.data;

      return { success: true, data: dataObj };
    } catch (err) {
      return { success: false };
    }
  };

  //This handles changing of user's password with the help of api
  const changeUserPasswordApiCall = async (options) => {
    try {
      const returnedData = await axios.post(
        `/users/auth/change-password`,
        options,
        {
          headers,
        },
      );

      showSnackbar(
        errorMessageHandler(returnedData?.data?.message),
        SNACKBAR_VARIANT.SUCCESS,
      );

      return { success: true };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  //This handles changing of user's profile picture with the help of api
  const changeUserProfilePictureApiCall = async (options) => {
    try {
      const returnedData = await axios.post(
        `/users/uploads/profile-picture`,
        options,
        {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        },
      );

      showSnackbar(
        errorMessageHandler(returnedData?.data?.message),
        SNACKBAR_VARIANT.SUCCESS,
      );

      return { success: true, data: returnedData?.data?.data };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  //This handles changing of user's cover picture with the help of api
  const changeUserCoverPictureApiCall = async (options) => {
    try {
      const returnedData = await axios.post(
        `/users/uploads/cover-picture`,
        options,
        {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        },
      );

      showSnackbar(
        errorMessageHandler(returnedData?.data?.message),
        SNACKBAR_VARIANT.SUCCESS,
      );

      return { success: true, data: returnedData?.data?.data };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  //This handles updating user profile information
  const updateUserProfileApiCall = async (profileData) => {
    try {
      const returnedData = await axios.put(
        `/users/profile`,
        profileData,
        { headers }
      );

      showSnackbar(
        errorMessageHandler(returnedData?.data?.message),
        SNACKBAR_VARIANT.SUCCESS,
      );

      // Update userData in context
      dispatch({
        type: UPDATE_USER_DATA,
        payload: returnedData?.data?.data,
      });

      return { success: true, data: returnedData?.data?.data };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  // User Status API calls
  const updateUserStatusApiCall = async (status) => {
    try {
      await axios.put("/user-status", { status }, { headers });
      // Silently update userData in context without snackbar
      dispatch({
        type: UPDATE_USER_DATA,
        payload: { status },
      });
      return { success: true };
    } catch (err) {
      return { success: false };
    }
  };

  //This handles the deleting of a room (only by the admin)
  const muteConversationApiCall = async (roomId) => {
    try {
      const { data } = await axios.patch(`/chat-rooms/conversations/${roomId}/mute`, {}, { headers });
      dispatch({ type: UPDATE_CHAT_PREF, payload: { roomId, is_muted: data.data.is_muted } });
      showSnackbar(data.data.is_muted ? "Notifications muted" : "Notifications unmuted", SNACKBAR_VARIANT.SUCCESS);
      return { success: true, data: data.data };
    } catch (err) {
      showSnackbar(errorMessageHandler(err?.response?.data?.message), SNACKBAR_VARIANT.ERROR);
      return { success: false };
    }
  };

  const archiveConversationApiCall = async (roomId) => {
    try {
      const { data } = await axios.patch(`/chat-rooms/conversations/${roomId}/archive`, {}, { headers });
      dispatch({ type: UPDATE_CHAT_PREF, payload: { roomId, is_archived: data.data.is_archived } });
      showSnackbar(data.data.is_archived ? "Chat archived" : "Chat unarchived", SNACKBAR_VARIANT.SUCCESS);
      return { success: true, data: data.data };
    } catch (err) {
      showSnackbar(errorMessageHandler(err?.response?.data?.message), SNACKBAR_VARIANT.ERROR);
      return { success: false };
    }
  };

  const softDeleteConversationApiCall = async (roomId) => {
    try {
      await axios.delete(`/chat-rooms/conversations/${roomId}`, { headers });
      dispatch({ type: REMOVE_CHAT, payload: { roomId } });
      showSnackbar("Conversation deleted", SNACKBAR_VARIANT.SUCCESS);
      return { success: true };
    } catch (err) {
      showSnackbar(errorMessageHandler(err?.response?.data?.message), SNACKBAR_VARIANT.ERROR);
      return { success: false };
    }
  };

  const setWallpaperApiCall = async (roomId, value) => {
    try {
      let response;
      if (value instanceof File) {
        const form = new FormData();
        form.append("wallpaper_image", value);
        response = await axios.patch(`/chat-rooms/conversations/${roomId}/wallpaper`, form, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await axios.patch(
          `/chat-rooms/conversations/${roomId}/wallpaper`,
          { wallpaper: value ?? null },
          { headers }
        );
      }
      const wallpaperUrl = response.data.data.chat_wallpaper;
      dispatch({ type: UPDATE_CHAT_PREF, payload: { roomId, chat_wallpaper: wallpaperUrl } });
      return { success: true, url: wallpaperUrl };
    } catch (err) {
      showSnackbar(errorMessageHandler(err?.response?.data?.message), SNACKBAR_VARIANT.ERROR);
      return { success: false };
    }
  };

  const uploadMessageImageApiCall = async (file) => {
    try {
      const form = new FormData();
      form.append("image", file);
      const response = await axios.post("/chat-rooms/upload-image", form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      return { success: true, url: response.data.data.url };
    } catch (err) {
      showSnackbar(errorMessageHandler(err?.response?.data?.message), SNACKBAR_VARIANT.ERROR);
      return { success: false };
    }
  };

  const setDisappearingIntervalApiCall = async (roomId, interval) => {
    try {
      await axios.patch(`/chat-rooms/conversations/${roomId}/disappearing`, { interval }, { headers });
      return { success: true };
    } catch (err) {
      return { success: false };
    }
  };

  const getDisappearingIntervalApiCall = async (roomId) => {
    try {
      const { data } = await axios.get(`/chat-rooms/conversations/${roomId}/disappearing`, { headers });
      return { success: true, interval: data?.data?.interval ?? null };
    } catch (err) {
      return { success: false, interval: null };
    }
  };

  const keepDisappearingMessageApiCall = async (messageId) => {
    try {
      await axios.patch(`/chat-rooms/messages/${messageId}/keep`, {}, { headers });
      cancelDisappearingTimer(messageId);
      return { success: true };
    } catch (err) {
      return { success: false };
    }
  };

  const unkeepDisappearingMessageApiCall = async (messageId) => {
    try {
      await axios.patch(`/chat-rooms/messages/${messageId}/keep`, { unkeep: true }, { headers });
      return { success: true };
    } catch (err) {
      return { success: false };
    }
  };

  const checkBlockStatusApiCall = async (targetUserId) => {
    try {
      const { data } = await axios.get(`/chat-rooms/users/${targetUserId}/block`, { headers });
      return { success: true, isBlocked: !!data?.data?.isBlocked };
    } catch (err) {
      return { success: false, isBlocked: false };
    }
  };

  const blockUserApiCall = async (targetUserId, targetUsername, roomId) => {
    try {
      await axios.post(`/chat-rooms/users/${targetUserId}/block`, {}, { headers });
      showSnackbar("You've blocked this person. They can no longer message you.", SNACKBAR_VARIANT.SUCCESS);
      if (roomId) {
        // Persist notice to localStorage so it survives refresh; only visible to blocker
        dispatch({
          type: ADD_BLOCKED_PERIOD_MESSAGE,
          payload: {
            roomId,
            message: {
              id: `block_notice_${Date.now()}`,
              message: `You blocked ${targetUsername || "this user"}`,
              is_system: true,
              sender_id: userData.id,
              created_at: new Date().toISOString(),
              room_id: roomId,
            },
          },
        });
      }
      return { success: true };
    } catch (err) {
      showSnackbar(errorMessageHandler(err?.response?.data?.message), SNACKBAR_VARIANT.ERROR);
      return { success: false };
    }
  };

  const unblockUserApiCall = async (targetUserId, targetUsername, roomId) => {
    try {
      await axios.delete(`/chat-rooms/users/${targetUserId}/block`, { headers });
      showSnackbar("You've unblocked this person.", SNACKBAR_VARIANT.SUCCESS);
      if (roomId) {
        dispatch({
          type: ADD_BLOCKED_PERIOD_MESSAGE,
          payload: {
            roomId,
            message: {
              id: `unblock_notice_${Date.now()}`,
              message: `You unblocked ${targetUsername || "this user"}`,
              is_system: true,
              sender_id: userData.id,
              created_at: new Date().toISOString(),
              room_id: roomId,
            },
          },
        });
      }
      return { success: true };
    } catch (err) {
      showSnackbar(errorMessageHandler(err?.response?.data?.message), SNACKBAR_VARIANT.ERROR);
      return { success: false };
    }
  };

  const reportUserApiCall = async (targetUserId, reason) => {
    try {
      await axios.post(`/chat-rooms/users/${targetUserId}/report`, { reason }, { headers });
      showSnackbar("Report submitted. Thank you.", SNACKBAR_VARIANT.SUCCESS);
      return { success: true };
    } catch (err) {
      showSnackbar(errorMessageHandler(err?.response?.data?.message), SNACKBAR_VARIANT.ERROR);
      return { success: false };
    }
  };

  const deleteChatRoonApiCall = async (roomId) => {
    try {
      await axios.delete(`/chat-rooms/rooms/${roomId}`, {
        headers,
      });
      showSnackbar(MESSAGE.ROOM_DELETED, SNACKBAR_VARIANT.SUCCESS);

      setTimeout(() => {
        return loadANewPage(ROUTES.dashboard);
      }, 2000);
    } catch (err) {
      return showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
    }
  };

  //This handles changing of user's activities with the help of api
  const getUserActivitiesApiCall = async (filter) => {
    try {
      const pageNo = filter?.pageNo ? Number(filter?.pageNo) : 1;
      const pageSize = filter?.pageSize ? Number(filter?.pageSize) : 1;

      const url = `/users/activities?pageNo=${pageNo}&pageSize=${pageSize}`;

      const returnedData = await axios.get(url, {
        headers,
      });

      return { success: true, data: returnedData?.data?.data };
    } catch (err) {
      // showSnackbar(
      //   errorMessageHandler(err?.response?.data?.message),
      //   SNACKBAR_VARIANT.ERROR
      // );
      return { success: false };
    }
  };

  const handleStripePaymentCheckout = async (packageId, customAmount = null) => {
    try {
      let url = `/payments/initiate-checkout/stripe?package_id=${packageId}`;

      // Add custom amount if provided
      if (customAmount && customAmount > 0) {
        url += `&custom_amount=${customAmount}`;
      }

      const returnedData = await axios.get(url, {
        headers,
      });

      const actualData = returnedData?.data?.data;

      // Initialize Stripe
      const stripe = await loadStripe(REACT_APP_STRIPE_PUBLIC_KEY);

      // Redirect to Checkout
      const result = await stripe.redirectToCheckout({
        sessionId: actualData.id,
      });

      // Handle any errors that may have occurred
      if (result.error) {
        showSnackbar(
          errorMessageHandler(result.error.message),
          SNACKBAR_VARIANT.ERROR,
        );
      }

      return { success: true, data: result };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  //This handles changing of user's email notification status with the help of api
  const toggleUserEmailNotificationStatusApiCall = async () => {
    try {
      const url = `/users/toggle-email-notifications`;

      const returnedData = await axios.get(url, {
        headers,
      });

      showSnackbar(returnedData?.data?.message, SNACKBAR_VARIANT.SUCCESS);

      return { success: true };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  //This handles changing of user's email notification status with the help of api
  const extendRoomExpirationTimeApiCall = async (roomId, quantity = 1) => {
    try {
      const url = `/chat-rooms/rooms/${roomId}/extend-time?quantity=${quantity}`;

      const returnedData = await axios.get(url, {
        headers,
      });

      const dataObj = returnedData.data.data;

      showSnackbar(returnedData?.data?.message, SNACKBAR_VARIANT.SUCCESS);

      dispatch({
        type: UPDATE_ACTIVE_CHAT_DATA,
        payload: dataObj,
      });

      return { success: true };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  //This handles payment of room screenshot with the help of api
  const payForRoomScreenshotApiCall = async (roomId, options) => {
    try {
      await axios.post(`/chat-rooms/rooms/${roomId}/screenshot/pay`, options, {
        headers,
      });

      return { success: true };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  //This handles reaction to room screenshot with the help of api
  const reactToRoomScreenshotApiCall = async (roomId, options) => {
    try {
      await axios.post(
        `/chat-rooms/rooms/${roomId}/screenshot/react`,
        options,
        {
          headers,
        },
      );

      return { success: true };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  // Get user membership info
  const getUserMembershipInfoApiCall = async () => {
    try {
      const returnedData = await axios.get("/membership/info", {
        headers,
      });

      return { success: true, data: returnedData.data.data };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  // Handle premium membership upgrade using coins
  const upgradeToPremiumMembershipApiCall = async () => {
    try {
      dataLoadinHandler(true);

      const returnedData = await axios.post(
        "/membership/change-tier",
        {},
        {
          headers,
        },
      );

      dataLoadinHandler(false);

      if (returnedData.data.success) {
        // Update the user data to reflect premium status and reduced coins
        const premiumCost = returnedData.data.data?.premium_cost || 10;

        const updatedUserData = {
          ...userData,
          account_type: "premium",
          available_coins: userData.available_coins - premiumCost,
        };

        dispatch({
          type: SET_USER_DATA,
          payload: updatedUserData,
        });

        showSnackbar(
          returnedData.data.message || "Successfully upgraded to premium!",
          SNACKBAR_VARIANT.SUCCESS,
        );
      }

      return {
        success: returnedData.data.success,
        data: returnedData.data.data,
      };
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  // Handle premium membership cancellation - benefits remain active until end date
  const cancelPremiumMembershipApiCall = async () => {
    try {
      dataLoadinHandler(true);

      const returnedData = await axios.post(
        "/membership/cancel",
        {},
        {
          headers,
        },
      );

      dataLoadinHandler(false);

      if (returnedData.data.success) {
        showSnackbar(
          returnedData.data.message ||
            "Premium membership cancelled. Your benefits will remain active until the end of your billing period.",
          SNACKBAR_VARIANT.SUCCESS,
        );
      }

      return { success: returnedData.data.success };
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  // Handle premium membership reactivation
  const reactivatePremiumMembershipApiCall = async () => {
    try {
      dataLoadinHandler(true);

      const returnedData = await axios.post(
        "/membership/reactivate",
        {},
        {
          headers,
        },
      );

      dataLoadinHandler(false);

      if (returnedData.data.success) {
        showSnackbar(returnedData.data.message, SNACKBAR_VARIANT.SUCCESS);
      }

      return returnedData.data;
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  // For Daily Bonus feature
  const claimDailyBonusApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const returnedData = await axios.post(
        "/daily-bonus/claim",
        {},
        {
          headers,
        },
      );
      dataLoadinHandler(false);

      // Update user coins in state
      if (returnedData.data.success) {
        showSnackbar(
          `Daily bonus of ${returnedData.data.data.bonus_amount} coins claimed!`,
          SNACKBAR_VARIANT.SUCCESS,
        );

        // Update user data with new coin balance
        if (returnedData.data.data.bonus_amount) {
          dispatch({
            type: UPDATE_USER_DATA,
            payload: {
              id: userData.id,
              available_coins:
                userData.available_coins + returnedData.data.data.bonus_amount,
            },
          });
        }
      }

      return { success: true, data: returnedData.data.data };
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  const checkDailyBonusStatusApiCall = async () => {
    try {
      const returnedData = await axios.get("/daily-bonus/status", {
        headers,
      });

      return { success: true, data: returnedData.data.data };
    } catch (err) {
      console.error("Error checking daily bonus status:", err);
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  // For Tier Management and Upgrades
  const createTierCheckoutSession = async (options) => {
    try {
      dataLoadinHandler(true);
      const returnedData = await axios.post(
        "/membership/change-tier",
        options,
        {
          headers,
        },
      );
      dataLoadinHandler(false);

      const actualData = returnedData?.data?.data;

      // Initialize Stripe
      const stripe = await loadStripe(REACT_APP_STRIPE_PUBLIC_KEY);

      // Redirect to Checkout
      const result = await stripe.redirectToCheckout({
        sessionId: actualData.session_id,
      });

      // Handle any errors that may have occurred
      if (result.error) {
        showSnackbar(
          errorMessageHandler(result.error.message),
          SNACKBAR_VARIANT.ERROR,
        );
      }

      return { success: true, data: result };
    } catch (err) {
      dataLoadinHandler(false);
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message || err?.message),
      };
    }
  };

  // Updates to existing membership functions to work with the new tier system
  const cancelMembershipApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const returnedData = await axios.post(
        "/membership/cancel",
        {},
        {
          headers,
        },
      );
      dataLoadinHandler(false);

      if (returnedData.data.success) {
        showSnackbar(
          returnedData.data.message || "Membership cancelled successfully",
          SNACKBAR_VARIANT.SUCCESS,
        );
      }

      return { success: true, message: returnedData.data.message };
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  const reactivateMembershipApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const returnedData = await axios.post(
        "/membership/reactivate",
        {},
        {
          headers,
        },
      );
      dataLoadinHandler(false);

      if (returnedData.data.success) {
        showSnackbar(
          returnedData.data.message || "Membership reactivated successfully",
          SNACKBAR_VARIANT.SUCCESS,
        );
      }

      return { success: true, data: returnedData.data.data };
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  // Toggle auto-renewal for membership
  const toggleAutoRenewApiCall = async () => {
    try {
      dataLoadinHandler(true);
      const returnedData = await axios.post(
        "/membership/auto-renew",
        {},
        {
          headers,
        },
      );
      dataLoadinHandler(false);

      if (returnedData.data.success) {
        showSnackbar(
          returnedData.data.message || "Auto-renewal setting updated",
          SNACKBAR_VARIANT.SUCCESS,
        );
      }

      return {
        success: returnedData.data.success,
        data: returnedData.data.data,
      };
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  // ===== AD MANAGEMENT API CALLS =====

  /**
   * Get active ads for user display
   * GET /ads/active/list
   */
  const getActiveAdsApiCall = async () => {
    try {
      const { data } = await axios.get("/ads/active/list", { headers });
      return { success: true, data: data.data || data };
    } catch (err) {
      console.error("Error fetching active ads:", err);
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  /**
   * Record ad view/impression
   * POST /ads/:adId/view
   */
  const recordAdViewApiCall = async (adId) => {
    try {
      const { data } = await axios.post(`/ads/${adId}/view`, {}, { headers });
      return { success: true, data: data.data || data };
    } catch (err) {
      console.error("Error recording ad view:", err);
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  /**
   * Record ad click
   * POST /ads/:adId/click
   */
  const recordAdClickApiCall = async (adId) => {
    try {
      const { data } = await axios.post(`/ads/${adId}/click`, {}, { headers });
      return { success: true, data: data.data || data };
    } catch (err) {
      console.error("Error recording ad click:", err);
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  /**
   * Get ad progress toward impression goal
   * GET /ads/:adId/progress
   */
  const getAdProgressApiCall = async (adId) => {
    try {
      const { data } = await axios.get(`/ads/${adId}/progress`, { headers });
      return { success: true, data: data.data || data };
    } catch (err) {
      console.error("Error fetching ad progress:", err);
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  // Update the existing checkAdStatusApiCall to match the pattern
  const checkAdStatusApiCall = async () => {
    try {
      const { data } = await axios.get("/ads/status", { headers });
      return { success: true, data: data.data || data };
    } catch (err) {
      console.error("Error checking ad status:", err);
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  // Update the existing markAdWatchedApiCall to match the pattern
  const markAdWatchedApiCall = async (roomId) => {
    try {
      dataLoadinHandler(true);
      const { data } = await axios.post(
        "/ads/watched",
        { room_id: roomId },
        { headers },
      );
      dataLoadinHandler(false);

      if (data.success) {
        showSnackbar(
          "Ad watch recorded successfully",
          SNACKBAR_VARIANT.SUCCESS,
        );
      }

      return { success: true, data: data.data || data };
    } catch (err) {
      dataLoadinHandler(false);
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  // ============================================
  // NOTIFICATION API CALLS
  // ============================================

  // Get user notifications with pagination and filters
  const getUserNotificationsApiCall = async (filters = {}) => {
    try {
      const params = {
        pageNo: filters.pageNo || 1,
        pageSize: filters.pageSize || 20,
      };

      if (filters.is_read !== undefined) params.is_read = filters.is_read;
      if (filters.type) params.type = filters.type;
      if (filters.priority) params.priority = filters.priority;

      const { data } = await axios.get("/user-notifications", {
        params,
        headers,
      });

      return { success: true, data: data.data };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return {
        success: false,
        message: errorMessageHandler(err?.response?.data?.message),
      };
    }
  };

  // Get unread notification count
  const getUnreadNotificationCountApiCall = async () => {
    try {
      const { data } = await axios.get("/user-notifications/unread-count", {
        headers,
      });

      return { success: true, count: data.data.unread_count };
    } catch (err) {
      return { success: false, count: 0 };
    }
  };

  // Mark notification as read
  const markNotificationAsReadApiCall = async (notificationId) => {
    try {
      await axios.post(
        "/user-notifications/mark-read",
        { notification_id: notificationId },
        { headers },
      );

      return { success: true };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  // Mark all notifications as read
  const markAllNotificationsAsReadApiCall = async () => {
    try {
      const { data } = await axios.post(
        "/user-notifications/mark-all-read",
        {},
        { headers },
      );

      showSnackbar(
        data.message || "All notifications marked as read",
        SNACKBAR_VARIANT.SUCCESS,
      );

      return { success: true, data: data.data };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  // ============================================
  // BOOKMARK API CALLS
  // ============================================

  // Bookmark a room
  const bookmarkRoomApiCall = async (roomId) => {
    try {
      const { data } = await axios.post(
        "/user-bookmarks",
        { room_id: roomId },
        { headers },
      );

      if (!data.data.already_bookmarked) {
        showSnackbar(
          data.message || "Room bookmarked successfully",
          SNACKBAR_VARIANT.SUCCESS,
        );
      }

      return { success: true, data: data.data };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  // Unbookmark a room
  const unbookmarkRoomApiCall = async (roomId) => {
    try {
      const { data } = await axios.delete(`/user-bookmarks/${roomId}`, {
        headers,
      });

      showSnackbar(
        data.message || "Room unbookmarked successfully",
        SNACKBAR_VARIANT.SUCCESS,
      );

      return { success: true };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  // Get user's bookmarked rooms
  const getUserBookmarkedRoomsApiCall = async (filters = {}) => {
    try {
      const params = {
        pageNo: filters.pageNo || 1,
        pageSize: filters.pageSize || 12,
      };

      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;
      if (filters.is_active !== undefined) params.is_active = filters.is_active;

      const { data } = await axios.get("/user-bookmarks", {
        params,
        headers,
      });

      return { success: true, data: data.data };
    } catch (err) {
      showSnackbar(
        errorMessageHandler(err?.response?.data?.message),
        SNACKBAR_VARIANT.ERROR,
      );
      return { success: false };
    }
  };

  // Check if room is bookmarked
  const checkRoomBookmarkStatusApiCall = async (roomId) => {
    try {
      const { data } = await axios.get(`/user-bookmarks/check/${roomId}`, {
        headers,
      });

      return { success: true, isBookmarked: data.data.is_bookmarked };
    } catch (err) {
      return { success: false, isBookmarked: false };
    }
  };

  // Get bookmark count
  const getBookmarkCountApiCall = async () => {
    try {
      const { data } = await axios.get("/user-bookmarks/count", {
        headers,
      });

      return { success: true, count: data.data.count };
    } catch (err) {
      return { success: false, count: 0 };
    }
  };

  return {
    appState,
    dispatch,
    popupNotifications,
    addPopupNotification,
    dismissPopupNotification,
    typingByRoom,
    localSystemMessages,
    addLocalSystemMessage,
    notificationSound,
    loginUserApiCall,
    registerUserApiCall,
    forgotPasswordApiCall,
    resetPasswordApiCall,
    VerifyAccountApiCall,
    GetAccountApiCall,
    logoutUser,
    resetApp,
    searchThroughChats,
    switchActiveChat,
    sendMessageToChat,
    joinARoom,
    createARoom,
    joinARoomApiCall,
    createARoomApiCall,
    fetchARoomByIdApiCall,
    showSnackbar,
    loadANewPage,
    adminCheckInUserIntoRoom,
    cancelJoinRequest,
    updateARoomTeamByIdApiCall,
    payRoomFeeForSpectatorModeByRoomIdApiCall,
    adminUpdateUserBanStatusApiCall,
    adminUpdateUserToolboxBanStatusApiCall,
    adminUpdateUserModeratorshipStatusApiCall,
    userKickOffParticipantVoteApiCall,
    deleteChatMessageApiCall,
    deleteMessageForMeApiCall,
    editChatMessageApiCall,
    updateChatMessageInteractionApiCall,
    updateChatMessageTipApiCall,
    fetchMoreRoomsAndMessagesApiCall,
    fetchMoreRoomMessagesApiCall,
    searchRoomMessagesApiCall,
    fetchAUserByIdApiCall,
    changeUserPasswordApiCall,
    changeUserProfilePictureApiCall,
    changeUserCoverPictureApiCall,
    updateUserProfileApiCall,
    updateExpiredActiveChat,
    deleteChatRoonApiCall,
    getUserActivitiesApiCall,
    handleStripePaymentCheckout,
    toggleUserEmailNotificationStatusApiCall,
    extendRoomExpirationTimeApiCall,
    toggleRoomToolbox,
    setCurrentUserRoomToolboxEnability,
    payForRoomScreenshotApiCall,
    reactToRoomScreenshotApiCall,
    getUserMembershipInfoApiCall,
    upgradeToPremiumMembershipApiCall,
    cancelPremiumMembershipApiCall,
    reactivatePremiumMembershipApiCall,

    claimDailyBonusApiCall,
    checkDailyBonusStatusApiCall,
    createTierCheckoutSession,
    cancelMembershipApiCall,
    reactivateMembershipApiCall,
    toggleAutoRenewApiCall,

    // Ad Management API calls
    getActiveAdsApiCall,
    recordAdViewApiCall,
    recordAdClickApiCall,
    getAdProgressApiCall,
    checkAdStatusApiCall,
    markAdWatchedApiCall,

    // Notification API calls
    getUserNotificationsApiCall,
    getUnreadNotificationCountApiCall,
    markNotificationAsReadApiCall,
    markAllNotificationsAsReadApiCall,

    // Bookmark API calls
    bookmarkRoomApiCall,
    unbookmarkRoomApiCall,
    getUserBookmarkedRoomsApiCall,
    checkRoomBookmarkStatusApiCall,
    getBookmarkCountApiCall,

    // Chat preference API calls
    muteConversationApiCall,
    archiveConversationApiCall,
    softDeleteConversationApiCall,
    setWallpaperApiCall,
    checkBlockStatusApiCall,
    setDisappearingIntervalApiCall,
    getDisappearingIntervalApiCall,
    keepDisappearingMessageApiCall,
    unkeepDisappearingMessageApiCall,
    blockUserApiCall,
    unblockUserApiCall,
    reportUserApiCall,
    uploadMessageImageApiCall,
  };
}

export default useApp;
